import {
  AutocompleteProps,
  Typography,
  FormLabel,
  FormHelperText,
  FormControl,
  Skeleton,
  Box,
  Grid,
  CircularProgress,
  BoxProps,
  AutocompleteOption,
} from '@mui/joy'
import { ParseKeys } from 'i18next'
import { uniqBy } from 'lodash'
import { SyntheticEvent, useMemo, useState } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Option } from '~/shared/config/constants'
import { useParserI18nMessage } from '~/shared/lib/i18n'
import { isString } from '~/shared/lib/utils'
import { SearchIcon } from '~/shared/ui/Icons'
import { ExternalChipList } from '../ui/ExternalChipList'
import { InternalChipList } from '../ui/InternalChipList'
import { Listbox } from '../ui/Listbox'
import { Autocomplete } from '../ui/styled'
type MultipleType = 'external' | 'internal'

type AutocompleteInputProps<
  T extends Option,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = {
  name: string
  label?: ParseKeys
  placeholder?: ParseKeys
  skeletonShow?: boolean
  options: T[]
  rules?: ControllerProps['rules']
  dataTestId?: string
  autocompleteBoxSx?: BoxProps['sx']
} & Partial<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>> &
  (
    | {
        multiple: true
        autocompleteXs?: number
        multipleType?: MultipleType
      }
    | {
        multiple?: false | undefined
        autocompleteXs?: never
        multipleType?: never
      }
  )

export function AutocompleteInput<T extends Option>({
  name,
  label,
  placeholder,
  skeletonShow,
  options,
  multiple,
  autocompleteXs,
  multipleType = 'external',
  autocompleteBoxSx,
  rules,
  dataTestId,
  ...props
}: AutocompleteInputProps<T, boolean, boolean, boolean>) {
  const { t } = useTranslation()
  const { parserI18nMessage } = useParserI18nMessage()

  const [search, setSearch] = useState<string>('')
  const [isOpen, setIsOpen] = useState(false)

  const layout = useMemo(() => {
    if (multiple && multipleType === 'external') {
      return {
        formControl: {
          component: Grid,
          xs: 12,
          sx: {
            justifyContent: 'space-between',
          },
        },
        autocompleteBox: {
          component: Grid,
          sx: {
            paddingTop: 0,
            paddingLeft: 0,
            paddingBottom: 0,
            ...autocompleteBoxSx,
          },
          xs: autocompleteXs,
        },
      }
    }
  }, [autocompleteBoxSx, autocompleteXs, multiple, multipleType])

  const handleSearchSet = (
    _: SyntheticEvent,
    value: string,
    reason: 'input' | 'reset' | 'clear',
  ) => {
    if (reason === 'input') {
      setSearch(value)
    }
    if (!multiple && (reason === 'reset' || reason === 'clear')) {
      setSearch('')
    }
  }

  const handleOnBlur = () => {
    setIsOpen(false)
    setSearch('')
  }

  const handleOnFocus = () => {
    setIsOpen(true)
  }

  return (
    <Controller
      name={name}
      defaultValue={multiple ? [] : null}
      rules={rules}
      render={({
        field: { onChange, value },
        fieldState: { error, invalid },
      }) => (
        <FormControl error={invalid} {...layout?.formControl}>
          {label && <FormLabel>{t(label)}</FormLabel>}

          {multiple && multipleType === 'external' && (
            <ExternalChipList<T>
              skeletonShow={skeletonShow}
              values={value || []}
              onChange={onChange}
              disabled={props?.readOnly}
            />
          )}

          <Box {...layout?.autocompleteBox}>
            <Autocomplete
              name={name}
              multiple={multiple}
              {...(multiple
                ? {
                    open: isOpen,
                    onBlur: handleOnBlur,
                    onFocus: handleOnFocus,
                    renderTags:
                      multipleType === 'internal'
                        ? (values: T[]) => (
                            <InternalChipList
                              values={values}
                              onChange={onChange}
                              disabled={props?.readOnly}
                            />
                          )
                        : () => null,
                  }
                : {})}
              value={multiple ? value || [] : value || null}
              size='lg'
              onChange={(_, value) => {
                onChange(value)
              }}
              clearOnBlur
              onInputChange={handleSearchSet}
              {...(multiple && { inputValue: search })}
              options={options}
              noOptionsText={
                <Typography color='warning'>
                  {t('alert.there are no options')}
                </Typography>
              }
              startDecorator={!props.readOnly && <SearchIcon />}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              slotProps={{
                ...(skeletonShow ? { root: { component: Skeleton } } : {}),
                listbox: {
                  component: Listbox,
                  params: {
                    onClick: () => {
                      onChange(
                        uniqBy([...(value || []), ...(options || [])], 'id'),
                      )
                    },
                    isSelectAll: multiple,
                  },
                },
              }}
              {...(placeholder
                ? { placeholder: t(placeholder as ParseKeys) }
                : {})}
              {...props}
              renderOption={(props, option) => {
                return (
                  <AutocompleteOption {...props} key={option.id}>
                    {parserI18nMessage(option.label as ParseKeys)}
                  </AutocompleteOption>
                )
              }}
              getOptionLabel={(option) =>
                isString(option)
                  ? option
                  : parserI18nMessage(option.label as ParseKeys)
              }
              endDecorator={
                props.loading ? (
                  <CircularProgress size='sm' />
                ) : (
                  props.endDecorator
                )
              }
            />
          </Box>

          {invalid && (
            <FormHelperText
              data-testid={`error-message-${dataTestId || (label && t(label))}`}
            >
              {parserI18nMessage(error?.message)}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}
